import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { InstructorAutocomplete } from '@/components/ui/InstructorAutocomplete';
import { InstructorDisplay } from '@/components/ui/InstructorDisplay';
import { WizardStepContainer } from './WizardStepContainer';
import { useI18n } from '@/contexts/I18nContext';

interface Instructor {
  id: string;
  full_name: string;
  username: string;
  profile_picture_url?: string;
  belt_rank?: string;
}

interface InstructorsStepProps {
  onNext: () => void;
  onBack: () => void;
  onComplete: () => void;
  currentStep: number;
  totalSteps: number;
  instructors: Instructor[];
  setInstructors: (instructors: Instructor[]) => void;
}

export const InstructorsStep: React.FC<InstructorsStepProps> = ({
  onNext,
  onBack,
  onComplete,
  currentStep,
  totalSteps,
  instructors,
  setInstructors
}) => {
  const { t } = useI18n();
  const [selectedInstructor, setSelectedInstructor] = useState<Instructor | string | null>(null);


  const handleInstructorSelect = (instructor: Instructor | string | null) => {
    setSelectedInstructor(instructor);
    if (!instructor) return;

    if (typeof instructor === 'object') {
      const newInstructor: Instructor = {
        id: instructor.id,
        full_name: instructor.full_name,
        username: instructor.username,
        profile_picture_url: instructor.profile_picture_url,
        belt_rank: instructor.belt_rank
      };

      const isAlreadyAdded = instructors.some(existing =>
        existing.id === newInstructor.id ||
        (existing.full_name === newInstructor.full_name && existing.username === newInstructor.username)
      );

      if (!isAlreadyAdded) {
        setInstructors([...instructors, newInstructor]);
      }
    } else {
      const newInstructor: Instructor = {
        id: `manual-${Date.now()}`,
        full_name: instructor,
        username: 'manual'
      };

      const isAlreadyAdded = instructors.some(existing =>
        existing.full_name === newInstructor.full_name && existing.username === 'manual'
      );

      if (!isAlreadyAdded) {
        setInstructors([...instructors, newInstructor]);
      }
    }

    // Clear selection to reset input in autocomplete
    setSelectedInstructor(null);
  };

  const handleRemoveInstructor = (instructorToRemove: Instructor) => {
    setInstructors(instructors.filter(instructor => instructor.id !== instructorToRemove.id));
  };

  const handleNext = () => {
    // Save instructors to profile
    // This will be handled by the parent component
    onNext();
  };

  return (
    <WizardStepContainer
      step={currentStep}
      totalSteps={totalSteps}
      onNext={handleNext}
      onComplete={onComplete}
      isLastStep={false}
    >
      <div className="space-y-6">
        {/* Title and description */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">
            {t('onboarding.instructors.title')}
          </h2>
          <p className="text-gray-600 text-sm">
            {t('onboarding.instructors.subtitle')}
          </p>
        </div>

        {/* Add instructor section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('onboarding.instructors.add_instructor')}
          </label>
          <div className="flex gap-2">
            <div className="flex-1">
              <InstructorAutocomplete
                value={selectedInstructor}
                onChange={handleInstructorSelect}
                placeholder={t('onboarding.instructors.search_placeholder')}
                className="w-full text-black border-black bg-white"
                containerClassName="w-full bg-white"
              />
            </div>
          </div>
        </div>

        {/* Current instructors list */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            {t('onboarding.instructors.your_instructors')} ({instructors.length})
          </h3>
          
          {instructors.length === 0 ? (
            <p className="text-gray-500 text-sm">
              {t('onboarding.instructors.no_results')}
            </p>
          ) : (
            <div className="space-y-3">
              {instructors.map((instructor) => (
                <div key={instructor.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <InstructorDisplay
                    instructor={{
                      id: instructor.id,
                      name: instructor.full_name,
                      username: instructor.username,
                      profile_picture_url: instructor.profile_picture_url,
                      belt_rank: instructor.belt_rank
                    }}
                    size="md"
                    className="flex-1"
                  />
                  
                  <button
                    onClick={() => handleRemoveInstructor(instructor)}
                    className="text-red-500 hover:text-red-700 p-1 ml-2"
                    title={t('onboarding.instructors.remove_instructor_title')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Help text */}
        <div className="text-sm text-gray-500">
          <p>{t('onboarding.instructors.help_search_users')}</p>
          <p>{t('onboarding.instructors.help_add_manual')}</p>
          <p>{t('onboarding.instructors.help_multiple')}</p>
        </div>
      </div>
    </WizardStepContainer>
  );
}; 